import { createContext, useContext, useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ru } from '../translations/ru'
import { en } from '../translations/en'

const LanguageContext = createContext()

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider')
  }
  return context
}

const translations = {
  ru,
  en,
}

export const LanguageProvider = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  
  const getLanguageFromPath = (path) => {
    if (path.startsWith('/eng')) return 'en'
    if (path.startsWith('/rus')) return 'ru'
    return null
  }

  const [language, setLanguage] = useState(() => {
    const path = window.location.pathname
    const langFromPath = getLanguageFromPath(path)
    if (langFromPath) return langFromPath
    const saved = localStorage.getItem('language')
    return saved || 'en'
  })

  useEffect(() => {
    const langFromPath = getLanguageFromPath(location.pathname)
    if (langFromPath && langFromPath !== language) {
      setLanguage(langFromPath)
      localStorage.setItem('language', langFromPath)
    }
  }, [location.pathname, language])

  useEffect(() => {
    localStorage.setItem('language', language)
    document.documentElement.lang = language
  }, [language])

  const toggleLanguage = () => {
    const newLang = language === 'ru' ? 'en' : 'ru'
    setLanguage(newLang)
    
    const currentPath = location.pathname
    const langPrefix = newLang === 'en' ? '/eng' : '/rus'
    
    let newPath = currentPath
    if (currentPath.startsWith('/eng') || currentPath.startsWith('/rus')) {
      newPath = currentPath.replace(/^\/(eng|rus)/, langPrefix)
    } else if (currentPath === '/') {
      newPath = langPrefix
    } else {
      newPath = langPrefix + currentPath
    }
    
    navigate(newPath, { replace: true })
  }

  const getLangPrefix = () => {
    const langFromPath = getLanguageFromPath(location.pathname)
    if (langFromPath) {
      return langFromPath === 'en' ? '/eng' : '/rus'
    }
    return language === 'en' ? '/eng' : '/rus'
  }

  const t = translations[language] || translations.en || translations.ru

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t, getLangPrefix }}>
      {children}
    </LanguageContext.Provider>
  )
}
